import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import {
    GET_ALL_PROJECTS_REQUESTED,
    GET_ALL_PLACES_REQUESTED,
    GET_ALL_ZONES_REQUESTED,
    GET_PLACES_REQUESTED,
    CREATE_PROJECT_REQUESTED,
    ASSIGN_PLACES_REQUESTED,
    ASSIGNEE_REQUESTED,
    RESEARCHERS_REQUESTED,
    EDIT_PLACE_REQUESTED,
    UNASSIGN_RESEARCHER_REQUESTED,
    DELETE_PLACE_REQUESTED,
    SET_FILTER_BY,
    SET_SORT_BY,
} from './constants';
import {
    getAllProjectsSucceeded,
    getAllProjectsFailed,
    getAllPlacesSucceeded,
    getAllPlacesFailed,
    getAllZonesSucceeded,
    getAllZonesFailed,
    getPlacesSucceeded,
    getPlacesFailed,
    createProjectSucceeded,
    createProjectFailed,
    assignUsersToPlacesSucceeded,
    assignUsersToPlacesFailed,
    researchersSucceeded,
    researchersFailed,
    assigneeSucceeded,
    assigneeFailed,
    editPlaceFailed,
    editPlaceSucceeded,
    deletePlaceSucceeded,
    deletePlaceFailed,
    unassignUserFromPlaceFailed,
    unassignUserFromPlaceSucceeded,
} from './actions';
import {
    getResearchProjects,
    getResearchPlaces,
    getProjectZones,
    createResearchProject,
    assignResearchPlaces,
    getCScientist,
    getPlaceDetail,
    editResearchPlace,
    unassignResearcher,
    deleteResearchPlace,
} from '../../../api';
import { parseClientError } from '../../../api/utils';
import {
    getLocationName,
    getSelectedProject,
    getSelectedZone,
    getStatus,
    getLimit,
    getSkip,
    getPlaces,
    getFilterBy,
    getSortBy,
    getSelectedPlacesState,
    getSelectedResearchers,
} from './selectors';
import { getPartnerId } from '../../Admin/selectors';
import { uploadCsv } from '../../../helpers/uploadImageToAWS';
import { getUserData } from '../../auth/selectors';
import { logException } from '../../../logger';

function* fetchAllProjects() {
    try {
        let projectsResponse = yield call(getResearchProjects, null);
        yield put(getAllProjectsSucceeded(projectsResponse));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(getAllProjectsFailed(message));
    }
}

function* fetchAllPlaces() {
    try {
        const params = {
            filterBy: 'name',
            sortBy: 'ASC',
        };
        const project = yield select(getSelectedProject);

        if (project) {
            params.projectId = project.id;
        }

        let placesResponse = yield call(getResearchPlaces, params);
        yield put(getAllPlacesSucceeded(placesResponse));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(getAllPlacesFailed(message));
    }
}

function* fetchAllZones() {
    try {
        const project = yield select(getSelectedProject);

        if (project) {
            let zonesResponse = yield call(getProjectZones, project.id);
            yield put(getAllZonesSucceeded(zonesResponse));
        } else {
            yield put(getAllZonesFailed('No project selected'));
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(getAllZonesFailed(message));
    }
}

function* fetchPlaces(action) {
    try {
        const { loadingMore } = action;

        const selectedPlaces = yield select(getSelectedPlacesState);
        const selectedResearchers = yield select(getSelectedResearchers);
        const project = yield select(getSelectedProject);
        const locationName = yield select(getLocationName);
        const zone = yield select(getSelectedZone);
        const status = yield select(getStatus);
        const limit = yield select(getLimit);
        const places = yield select(getPlaces);
        const filterBy = yield select(getFilterBy);
        const sortBy = yield select(getSortBy);
        let skip = yield select(getSkip);
        skip = loadingMore ? skip + limit : 0;
        const params = { skip, limit };

        if (selectedPlaces && selectedPlaces.length) {
            params.placeIds = selectedPlaces;
        }
        if (selectedResearchers && selectedResearchers.length) {
            params.assigneeUserId = selectedResearchers.join(',');
        }
        if (project) {
            // params.name = project.name; //Deprecated param - filtering by Project id is better than name
            params.projectId = project.id;
        }
        if (zone) {
            params.zoneId = zone.id;
        }
        if (locationName) {
            params.address = locationName;
        }
        if (status && status.id && status.id != 'ALL') {
            params.status = status.id;
        }
        if (filterBy) {
            params.filterBy = filterBy;
        }
        if (sortBy) {
            params.sortBy = sortBy;
        }

        let placesResponse = yield call(getResearchPlaces, params);
        const hasMorePlaces = !!placesResponse.length;
        if (loadingMore) {
            placesResponse = [...places, ...placesResponse];
        }
        yield put(
            getPlacesSucceeded({
                places: placesResponse,
                skip,
                hasMorePlaces,
            }),
        );
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(getPlacesFailed(message));
    }
}

function* createProject(action) {
    try {
        const { name, file } = action;
        const csvPath = yield call(uploadCsv, file);
        const response = yield call(createResearchProject, { name, csvPath });
        if (response.errorMessages) {
            yield put(createProjectFailed(response.errorMessages));
        } else {
            yield put(createProjectSucceeded(response));
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(createProjectFailed(message));
    }
}

function* assignPlaces(action) {
    try {
        const { projectId, placeIds, researchers } = action;
        const response = yield call(assignResearchPlaces, {
            projectId,
            placeIds,
            researcherIds: researchers.map((researcher) => researcher.id),
        });

        if (response.errorMsgs && response.errorMsgs.length) {
            yield put(assignUsersToPlacesFailed(response.errorMsgs[0]));
        } else {
            yield put(assignUsersToPlacesSucceeded({ placeIds, researchers }));
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(assignUsersToPlacesFailed(message));
    }
}

function* getResearchers() {
    try {
        const partnerId = yield select(getPartnerId);
        let researchers = yield call(getCScientist, partnerId);
        researchers = researchers.data.users || [];
        yield put(researchersSucceeded(researchers));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(researchersFailed(message));
    }
}

function* getAssignees(action) {
    try {
        const { placeId } = action;
        let placeDetail = yield call(getPlaceDetail, placeId);
        yield put(assigneeSucceeded(placeDetail.data));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, placeId: action.placeId });

        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(assigneeFailed(message));
    }
}

function* editPlace(action) {
    try {
        const { placeId, name, lat, long } = action;
        let placeDetail = yield call(editResearchPlace, {
            placeId,
            name,
            lat,
            long,
        });
        yield put(editPlaceSucceeded(placeDetail));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, placeId: action.placeId });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(editPlaceFailed(message));
    }
}

function* deletePlace(action) {
    try {
        const { placeId } = action;
        yield call(deleteResearchPlace, { placeId });
        yield put(deletePlaceSucceeded(placeId));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, placeId: action.placeId });

        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(deletePlaceFailed(message));
    }
}

function* unassignUserFromPlace(action) {
    try {
        const { projectId, placeId, researcherId } = action;
        yield call(unassignResearcher, {
            projectId,
            placeId,
            researcherId,
        });
        yield put(
            unassignUserFromPlaceSucceeded(projectId, placeId, researcherId),
        );
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, {
            ...userData,
            ...action,
        });

        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(unassignUserFromPlaceFailed(message));
    }
}

export default function*() {
    yield all([
        yield takeLatest(GET_ALL_PROJECTS_REQUESTED, fetchAllProjects),
        yield takeLatest(GET_ALL_PLACES_REQUESTED, fetchAllPlaces),
        yield takeLatest(GET_ALL_ZONES_REQUESTED, fetchAllZones),
        yield takeLatest(GET_PLACES_REQUESTED, fetchPlaces),
        yield takeLatest(SET_FILTER_BY, fetchPlaces),
        yield takeLatest(SET_SORT_BY, fetchPlaces),
        yield takeLatest(CREATE_PROJECT_REQUESTED, createProject),
        yield takeLatest(ASSIGN_PLACES_REQUESTED, assignPlaces),
        yield takeLatest(RESEARCHERS_REQUESTED, getResearchers),
        yield takeLatest(ASSIGNEE_REQUESTED, getAssignees),
        yield takeLatest(EDIT_PLACE_REQUESTED, editPlace),
        yield takeLatest(DELETE_PLACE_REQUESTED, deletePlace),
        yield takeLatest(UNASSIGN_RESEARCHER_REQUESTED, unassignUserFromPlace),
    ]);
}
